if (!customElements.get("product-form")) {
  customElements.define(
    "product-form",
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector("form");
        this.variantIdInput.disabled = false;
        this.form.addEventListener("submit", this.onSubmitHandler.bind(this));
        this.cart = document.querySelector("cart-drawer");
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector("span");

        if (document.querySelector("cart-drawer"))
          this.submitButton.setAttribute("aria-haspopup", "dialog");

        this.hideErrors = this.dataset.hideErrors === "true";
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute("aria-disabled") === "true") return;

        this.handleErrorMessage();

        this.submitButton.setAttribute("aria-disabled", true);
        this.submitButton.classList.add("loading");
        this.querySelector(".loading__spinner").classList.remove("hidden");

        const formData = new FormData(this.form);
        const variantId = formData.get("id");
        const quantity = parseInt(formData.get("quantity")) || 1;

        if (this.cart) {
          this.cart.setActiveElement(document.activeElement);

          const optimisticData = this.getOptimisticProductData(variantId, quantity);
          if (optimisticData && this.cart.addOptimisticItem) {
            // Paint the item from data already on the page instead of waiting on
            // the request - no loading spinner, the drawer just shows the add.
            this.cart.addOptimisticItem(optimisticData);
          } else {
            // Fallback for add buttons that don't expose product data (eg. quick
            // buy, upsell cards): open right away and show a loading state so the
            // drawer still feels instant even on a slow connection.
            this.cart.open?.();
            this.cart.setLoading?.(true);
          }
        }

        const config = this.buildAddToCartConfig(formData, variantId, quantity);

        const linesUpdateDeferred = this.createCartLinesUpdateEvent(
          variantId,
          quantity,
        );

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              this.cart?.setLoading?.(false);
              this.cart?.removeOptimisticItem?.();
              publish(PUB_SUB_EVENTS.cartError, {
                source: "product-form",
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.dispatchCartErrorEvent(
                response.description || response.message,
                "INVALID",
              );
              linesUpdateDeferred?.reject(
                new Error(response.description || response.message),
              );

              const soldOutMessage =
                this.submitButton.querySelector(".sold-out-message");
              if (!soldOutMessage) return;
              this.submitButton.setAttribute("aria-disabled", true);
              this.submitButtonText.classList.add("hidden");
              soldOutMessage.classList.remove("hidden");
              this.error = true;
              return;
            } else if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);

            const startMarker = CartPerformance.createStartingMarker(
              "add:wait-for-subscribers",
            );
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: "product-form",
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker(
                  "add:wait-for-subscribers",
                  startMarker,
                );
              });
            this.error = false;
            const quickAddModal = this.closest("quick-add-modal");
            if (quickAddModal) {
              document.body.addEventListener(
                "modalClosed",
                () => {
                  setTimeout(() => {
                    CartPerformance.measure(
                      "add:paint-updated-sections",
                      () => {
                        this.cart.renderContents(response);
                      },
                    );
                  });
                },
                { once: true },
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            this.cart?.setLoading?.(false);
            this.cart?.removeOptimisticItem?.();
            this.dispatchCartErrorEvent(
              e.message || "Network error",
              "SERVICE_UNAVAILABLE",
            );
            linesUpdateDeferred?.reject(e);
          })
          .finally(() => {
            this.submitButton.classList.remove("loading");
            if (this.cart && this.cart.classList.contains("is-empty"))
              this.cart.classList.remove("is-empty");
            if (!this.error) this.submitButton.removeAttribute("aria-disabled");
            this.querySelector(".loading__spinner").classList.add("hidden");

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      // Free-gift rules that qualify from the first item alone (rule <= 0), read
      // straight from the drawer's data-cart-total attribute - no cart fetch needed,
      // since that attribute is kept in sync with every drawer render (see cart-drawer.js).
      // Only applies when the cart is currently empty: any other case can't be
      // predicted client-side (the existing item total is unknown here), so it falls
      // back to the normal single-item add + background gift sync.
      getEmptyCartFreeGifts() {
        if (!this.cart || this.cart.dataset.cartTotal !== "0") return [];

        const drawerData = window.Shopify && window.Shopify.cart_drawer;
        if (!drawerData) return [];

        return [drawerData.freeProduct1, drawerData.freeProduct2].filter(
          (config) =>
            config &&
            config.variantId &&
            config.available === "true" &&
            Number(config.rule) <= 0,
        );
      }

      // Reads the data-product-* attributes rendered onto this element (see
      // buy-buttons.liquid / mystery-box-grid.liquid) so the drawer can paint the
      // add instantly instead of waiting for the request. Returns null when a
      // template hasn't been updated with those attributes yet (eg. quick buy,
      // upsell cards), so those flows keep the old open+spinner behavior.
      getOptimisticProductData(variantId, quantity) {
        const title = this.dataset.productTitle;
        if (!title) return null;

        return {
          variantId,
          title,
          image: this.dataset.productImage || "",
          price: this.dataset.productPrice || "",
          priceCents: Number(this.dataset.productPriceCents) || 0,
          quantity,
        };
      }

      buildAddToCartConfig(formData, variantId, quantity) {
        const gifts = this.getEmptyCartFreeGifts();

        if (!gifts.length) {
          const config = fetchConfig("javascript");
          config.headers["X-Requested-With"] = "XMLHttpRequest";
          delete config.headers["Content-Type"];

          if (this.cart) {
            formData.append(
              "sections",
              this.cart.getSectionsToRender().map((section) => section.id),
            );
            formData.append("sections_url", window.location.pathname);
          }
          config.body = formData;
          return config;
        }

        // Cart is empty and a gift already qualifies from this add alone - include
        // it in the same /cart/add.js call instead of adding the item, then fetching
        // the cart, then sending a second update request to add the gift.
        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";

        const items = [{ id: variantId, quantity }];
        gifts.forEach((gift) => items.push({ id: Number(gift.variantId), quantity: 1 }));

        const payload = { items };
        if (this.cart) {
          payload.sections = this.cart.getSectionsToRender().map((section) => section.id);
          payload.sections_url = window.location.pathname;
        }
        config.body = JSON.stringify(payload);
        return config;
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector(".product-form__error-message-wrapper");
        if (!this.errorMessageWrapper) return;
        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            ".product-form__error-message",
          );

        this.errorMessageWrapper.toggleAttribute("hidden", !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute("disabled", "disabled");
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute("disabled");
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: "add",
            context: "product",
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          }),
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise =
          typeof CartItems !== "undefined"
            ? CartItems.fetchCartData()
            : fetch(`${routes.cart_url}.json`).then((response) =>
                response.json(),
              );

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency)
              return deferred.reject(
                new Error("Missing currency in cart response"),
              );
            deferred.resolve({
              cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
            });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector("[name=id]");
      }
    },
  );
}
